import { CreateInspectionResponse } from '@services';
import { useStore } from '@stores';
import { useCallback, useEffect, useRef, useState } from 'react';

export type InspectionSaveState = 'idle' | 'saving' | 'saved' | 'error';

export interface IInspectionAutosaveHook {
  state: InspectionSaveState;
  lastSavedAt?: Date;
  /** Anything typed but not yet on the server, including whatever a failed save is holding on to. */
  hasUnsaved: boolean;
  queue: (subCategoryItemId: number, values: CreateInspectionResponse, immediate?: boolean) => void;
  /** Resolves false if anything is still waiting to go after the attempt. */
  flush: () => Promise<boolean>;
}

/** How long to sit on a note before sending it, so a sentence is one save and not thirty. */
const DEBOUNCE_MS = 2000;

/**
 * Saves checklist items on their own, as they are answered.
 *
 * A whole inspection used to go up on every click of Save - 45 rows and every photo, whether or not
 * they had been touched - which is what timed out, and what made people press Save after each field
 * in the first place. One item is a couple of hundred bytes, so a full checklist costs less over
 * twenty minutes than one of the old saves.
 *
 * Saves are chained rather than fired in parallel: two writes to the same item overlapping is how the
 * later one loses. The API keys a response on (inspection, item), so a resend is an update, never a
 * second row - which is what makes retrying safe.
 */
export const useInspectionAutosave = (inspectionId: string | undefined): IInspectionAutosaveHook => {
  const { inspectionStore } = useStore();

  const [state, setState] = useState<InspectionSaveState>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<Date | undefined>();
  const [hasUnsaved, setHasUnsaved] = useState(false);

  const pendingRef = useRef<Map<number, CreateInspectionResponse>>(new Map());
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const chainRef = useRef<Promise<void>>(Promise.resolve());
  const mountedRef = useRef(true);

  useEffect(
    () => () => {
      mountedRef.current = false;
      timerRef.current && clearTimeout(timerRef.current);
    },
    []
  );

  const flush = useCallback(async (): Promise<boolean> => {
    timerRef.current && clearTimeout(timerRef.current);

    chainRef.current = chainRef.current.then(async () => {
      if (!inspectionId || pendingRef.current.size === 0) return;

      const batch = Array.from(pendingRef.current.values());
      pendingRef.current.clear();

      mountedRef.current && setState('saving');

      try {
        await inspectionStore.createResponses({ id: inspectionId, responses: batch });

        if (!mountedRef.current) return;

        setLastSavedAt(new Date());
        setState(pendingRef.current.size > 0 ? 'saving' : 'saved');
      } catch {
        // Put the work back rather than dropping it, but never over the top of something newer the
        // user has typed since. The next save - or Done - carries it again.
        batch.forEach((item) => {
          if (!pendingRef.current.has(item.subCategoryItemId))
            pendingRef.current.set(item.subCategoryItemId, item);
        });

        mountedRef.current && setState('error');
      } finally {
        mountedRef.current && setHasUnsaved(pendingRef.current.size > 0);
      }
    });

    await chainRef.current;

    return pendingRef.current.size === 0;
  }, [inspectionId, inspectionStore]);

  const queue = useCallback(
    (subCategoryItemId: number, values: CreateInspectionResponse, immediate = false) => {
      pendingRef.current.set(subCategoryItemId, { ...values, subCategoryItemId });
      setHasUnsaved(true);

      timerRef.current && clearTimeout(timerRef.current);

      if (immediate) {
        void flush();
        return;
      }

      timerRef.current = setTimeout(() => void flush(), DEBOUNCE_MS);
    },
    [flush]
  );

  return { state, lastSavedAt, hasUnsaved, queue, flush };
};
